"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.devicePowerSchema = exports.deviceListQuerySchema = exports.setDeviceServiceStateSchema = exports.tvTestSchema = exports.ASSIGN_DEVICE_GAME_REFUSALS = exports.assignDeviceGameSchema = exports.reorderDevicesSchema = exports.updateDeviceSchema = exports.createDeviceSchema = exports.updateDeviceCategorySchema = exports.createDeviceCategorySchema = exports.RESERVATION_TYPES = exports.DEVICE_STATUSES = exports.DEVICE_TYPES = void 0;
exports.reservationUsesAccountBalance = reservationUsesAccountBalance;
const zod_1 = require("zod");
const network_1 = require("./network");
/**
 * Device contracts — SPEC §5.2 (Device List) and §6.2 (Device Categories).
 *
 * The enums here mirror the Prisma enums in `@gc/db`. They are duplicated
 * rather than imported because the web apps must not pull in the Prisma client,
 * which is server-only. `devices.contract.test.ts` in the API asserts the two
 * stay in step, so a drift fails the build rather than shipping.
 *
 * MONEY OVER THE WIRE: every `*Md` field is a bigint of millidinars on the
 * server. JSON has no bigint, so it crosses as a decimal STRING and is parsed
 * back with `fromMillidinars()`. Never `Number()` these — a large venue total
 * would silently lose precision.
 */
/* -------------------------------------------------------------------------- */
/* Enums                                                                      */
/* -------------------------------------------------------------------------- */
/** Physical kind of device. Drives free-mode eligibility and order destinations. */
exports.DEVICE_TYPES = [
    'PC',
    'CONSOLE',
    'BILLIARD',
    'TABLE',
    'ARCADE',
    'CINEMA',
    'OTHER',
];
/**
 * May a sitting on this kind of machine run against a customer's BALANCE?
 *
 * PCs only. A PC sitting is funded by the account: the portal counts the
 * balance down in front of the customer and the expiry sweep ends the sitting
 * when it runs out. Everything else in the venue — consoles, billiards, tables,
 * the cinema — is reserved under a NAME, its clock counts UP, and it is settled
 * at the counter when the party finishes (owner: "it has nothing to do with
 * account balance").
 *
 * SHARED, because the rule is asked on both sides of the wire and the two must
 * not drift. The staff drawer asks it to decide whether an account may be
 * attached at all; the portal clock asks it to decide whether a balance may be
 * counted down. Guarding only the first left the second wide open: a PS5 put on
 * a registered customer's TAB — which is what picking a name from the "here
 * now" list does — inherited the account through the tab, started counting his
 * money down, and could be closed by the sweep over a balance the venue was
 * never charging that game against.
 */
function reservationUsesAccountBalance(deviceType) {
    return deviceType === 'PC';
}
/** SPEC §5.2 — the four states the Device List filters on. */
exports.DEVICE_STATUSES = ['AVAILABLE', 'BUSY', 'ABOUT_TO_END', 'OUT_OF_SERVICE'];
/** Hourly devices bill prorated time; round devices bill per game. */
exports.RESERVATION_TYPES = ['HOURLY', 'ROUND'];
/* -------------------------------------------------------------------------- */
/* Write models                                                               */
/* -------------------------------------------------------------------------- */
/** Money accepted from clients as whole IQD, converted server-side. */
const iqdAmount = zod_1.z
    .number()
    .int('Prices are whole dinars — IQD has no useful sub-unit.')
    .min(0, 'Price cannot be negative.')
    .max(1_000_000_000);
/**
 * Picture of this actual machine, uploaded from the Add/Edit Device dialog.
 * Omitted or cleared leaves the device on the built-in artwork for its type, so
 * "no picture" is a real answer rather than a blank tile.
 */
const deviceImageId = zod_1.z
    .string()
    .trim()
    .max(40)
    .nullable()
    // '' is what an emptied form field sends; it must clear rather than reach
    // Prisma as a foreign key that matches nothing.
    .transform((value) => (value ? value : null))
    // `.optional()` stays OUTERMOST: with a transform on the outside, `z.input`
    // loses the key's optionality and every caller is forced to spell the key.
    .optional();
/**
 * Device categories (SPEC §6.2) — the grouping every device must belong to, and
 * which printer its tickets route to.
 *
 * These write schemas were missing entirely. The API exposed only a GET, so a
 * venue starting from an empty database could not create a category — and
 * `createDeviceSchema` below REQUIRES one, so the first device could never be
 * added at all. Only the demo seed writing categories straight into the
 * database hid it.
 */
exports.createDeviceCategorySchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Category name is required.').max(60),
    /** Null routes tickets to the venue default printer rather than a specific one. */
    printerId: zod_1.z
        .string()
        .trim()
        .max(40)
        .nullable()
        .optional()
        .transform((value) => (value ? value : null)),
    sortOrder: zod_1.z.number().int().min(0).default(0),
});
/**
 * Declared explicitly rather than as `.partial()` of the create shape: in zod 4
 * a `.default()` survives `.partial()`, so `sortOrder` would silently reset to 0
 * every time someone renamed a category. `printerId` distinguishes omitted
 * (leave it alone) from null/'' (clear to the venue default).
 */
exports.updateDeviceCategorySchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(60).optional(),
    printerId: zod_1.z
        .string()
        .trim()
        .max(40)
        .nullable()
        .optional()
        .transform((value) => (value === undefined ? undefined : value ? value : null)),
    sortOrder: zod_1.z.number().int().min(0).optional(),
});
/**
 * §7 TV control — what may be saved as a TV's address.
 *
 * Unvalidated free text put `000000` in a live venue row (2026-08-23): the
 * bridge dutifully tried to drive a TV at "000000" forever while the seat's
 * real television sat unclaimed, and nothing in the app ever said so. An
 * address is an IPv4 dotted quad (no leading zeros — Windows and adb read
 * those as octal) or an IPv6 literal; a hostname is refused because the
 * runbook hands out static leases and a name that fails to resolve is the
 * same silent failure by another route.
 */
const TV_ADDRESS_MESSAGE = "Enter the TV's address on the venue network, like 192.168.1.50.";
/** 0.0.0.0 is a wildcard, not a television. */
const UNUSABLE_ADDRESSES = new Set(['0.0.0.0', '255.255.255.255']);
function isTvAddress(value) {
    // IPv4 ONLY. The first pass allowed an IPv6-shaped string, and the pattern
    // was loose enough to bless `::` and `1:2:3` (review finding) — which is
    // how `000000` got in in the first place. Every venue LAN this runs on
    // hands out v4 leases, and the bridge builds `ip:5555` for adb, so a v6
    // literal would need bracketing it never gets. The dotted-quad rule is the
    // one the Central Device IP uses (`isIpv4Address`); a TV also cannot be a
    // wildcard or the broadcast address.
    return (0, network_1.isIpv4Address)(value) && !UNUSABLE_ADDRESSES.has(value);
}
/** The schema-side test: absent and cleared are both fine, garbage is not. */
function isTvAddressOrEmpty(value) {
    return value === null || value === undefined || isTvAddress(value);
}
/**
 * Add Device — the field list is fixed by SPEC §5.2 and must not be trimmed:
 * Device name · Tag · Device type · Reservation type · Hourly price ·
 * Single game price · Double price · Category · Enable free mode.
 */
exports.createDeviceSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(1, 'Device name is required.').max(60),
    tag: zod_1.z.string().trim().max(60).optional().nullable(),
    deviceType: zod_1.z.enum(exports.DEVICE_TYPES),
    categoryId: zod_1.z.string().min(1, 'Category is required.'),
    reservationType: zod_1.z.enum(exports.RESERVATION_TYPES).default('HOURLY'),
    hourlyPrice: iqdAmount.default(0),
    singleGamePrice: iqdAmount.default(0),
    doublePrice: iqdAmount.default(0),
    threeBallPrice: iqdAmount.optional().nullable(),
    freeModeEnabled: zod_1.z.boolean().default(false),
    isHidden: zod_1.z.boolean().default(false),
    sortOrder: zod_1.z.number().int().min(0).default(0),
    imageMediaAssetId: deviceImageId,
    /** §7 TV control: the TV's static LAN IP; empty clears to "no TV". */
    tvIpAddress: zod_1.z
        .string()
        .trim()
        .max(64)
        .optional()
        .nullable()
        .transform((value) => (value ? value : null))
        .refine(isTvAddressOrEmpty, { message: TV_ADDRESS_MESSAGE }),
    tvHdmiInput: zod_1.z.number().int().min(1).max(4).optional().nullable(),
})
    .refine((value) => !value.freeModeEnabled || value.deviceType === 'PC', {
    // Free mode drops the client straight to the Windows desktop, which only
    // exists on a PC running the agent (SPEC §5.2, §9).
    message: 'Free mode applies to PCs only.',
    path: ['freeModeEnabled'],
})
    .refine((value) => value.reservationType !== 'ROUND' || value.singleGamePrice > 0, {
    message: 'A round-based device needs a single game price.',
    path: ['singleGamePrice'],
});
/** Partial update. Same rules, re-checked server-side against the merged row. */
exports.updateDeviceSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(60).optional(),
    tag: zod_1.z.string().trim().max(60).optional().nullable(),
    deviceType: zod_1.z.enum(exports.DEVICE_TYPES).optional(),
    categoryId: zod_1.z.string().min(1).optional(),
    reservationType: zod_1.z.enum(exports.RESERVATION_TYPES).optional(),
    hourlyPrice: iqdAmount.optional(),
    singleGamePrice: iqdAmount.optional(),
    doublePrice: iqdAmount.optional(),
    threeBallPrice: iqdAmount.optional().nullable(),
    freeModeEnabled: zod_1.z.boolean().optional(),
    isActive: zod_1.z.boolean().optional(),
    isHidden: zod_1.z.boolean().optional(),
    sortOrder: zod_1.z.number().int().min(0).optional(),
    /**
     * OMITTED must stay `undefined` so a rename leaves the picture alone; an
     * explicit null clears it back to the type template. Same distinction the
     * cafe update schemas draw (wave-3 review FIX-2).
     */
    imageMediaAssetId: deviceImageId,
    /** §7 TV control. Omitted leaves the TV alone; null / '' clears it. */
    tvIpAddress: zod_1.z
        .string()
        .trim()
        .max(64)
        .nullable()
        .transform((value) => (value ? value : null))
        .refine(isTvAddressOrEmpty, { message: TV_ADDRESS_MESSAGE })
        // Outermost for the same reason as `deviceImageId` above.
        .optional(),
    tvHdmiInput: zod_1.z.number().int().min(1).max(4).optional().nullable(),
});
/**
 * Arrange the board (Edit layout).
 *
 * The whole visible order in one call rather than a PATCH per tile: dragging
 * one device into a new slot shifts every device after it, and a dozen separate
 * writes would leave the board readable only if all of them landed. Ids are
 * sent in their new order; the server numbers them from that.
 */
exports.reorderDevicesSchema = zod_1.z.object({
    deviceIds: zod_1.z.array(zod_1.z.string().min(1)).min(1).max(500),
});
/**
 * "Set game…" on the Screens page (owner, 2026-09-24): tick PCs, choose the
 * game they are set up for — or `null`, "No game". Each PC then plays that
 * game's video on its lock screen, so the owner sets a video once per game
 * and every PC of that game follows it.
 *
 * Many PCs in one call because that is how the venue is laid out (a row of
 * Fortnite PCs, a row of BO6 PCs), and one request is one all-or-nothing
 * answer: a tick list that half-applied would leave the owner guessing which
 * PCs moved. 200 is several times the venue's PC count. A PC listed twice is
 * refused rather than folded, the same rule `bulkAssignHomeDevicesSchema`
 * keeps: the list the client sent is the list the server applies.
 */
exports.assignDeviceGameSchema = zod_1.z.object({
    deviceIds: zod_1.z
        .array(zod_1.z.string().trim().min(1).max(64))
        .min(1, 'Tick at least one PC.')
        .max(200)
        .refine((ids) => new Set(ids).size === ids.length, 'Each PC may appear only once.'),
    gameId: zod_1.z.string().trim().min(1).max(64).nullable(),
});
/**
 * The refusals "Set game…" answers with, as `{ code, message }`. All refuse
 * the WHOLE request, before anything is written.
 *
 * - `device_not_found`: a ticked PC is not this venue's (deleted since the
 *   page loaded, or never was) — reload.
 * - `game_not_found`: the chosen game is not this venue's — reload.
 * - `device_not_pc`: a ticked device is a console, table or cinema. A PS5's
 *   video plays on its TV and stays a Screens choice; only a PC has a lock
 *   screen for a game's video to play on.
 */
exports.ASSIGN_DEVICE_GAME_REFUSALS = [
    'device_not_found',
    'game_not_found',
    'device_not_pc',
];
/**
 * §7 TV control — the staff Test buttons: force this device's TV to one mode
 * so the owner can watch the wall react while setting a TV up. The next real
 * transition (or the bridge reconnecting) re-asserts computed truth.
 */
exports.tvTestSchema = zod_1.z.object({
    mode: zod_1.z.enum(['idle', 'console']),
});
/** Mark a device out of service, or bring it back (SPEC §5.2 tile action). */
exports.setDeviceServiceStateSchema = zod_1.z.object({
    outOfService: zod_1.z.boolean(),
    reason: zod_1.z.string().trim().max(200).optional().nullable(),
});
/* -------------------------------------------------------------------------- */
/* Query                                                                      */
/* -------------------------------------------------------------------------- */
/** SPEC §5.2 filters: category, type, and state. */
exports.deviceListQuerySchema = zod_1.z.object({
    categoryId: zod_1.z.string().min(1).optional(),
    deviceType: zod_1.z.enum(exports.DEVICE_TYPES).optional(),
    status: zod_1.z.enum(exports.DEVICE_STATUSES).optional(),
    /** Free-text match on name or tag. */
    search: zod_1.z.string().trim().max(60).optional(),
    /** Include devices switched off in settings. Defaults to false. */
    includeInactive: zod_1.z.coerce.boolean().default(false),
    /**
     * Include devices hidden from the board. Defaults to false, so the board a
     * venue actually watches all day is the one that has to ask for nothing.
     * Settings turns this on so a hidden device can still be found and unhidden.
     */
    includeHidden: zod_1.z.coerce.boolean().default(false),
});
/* -------------------------------------------------------------------------- */
/* Wave 3 — power control                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Staff body for `POST /devices/:id/power`. The confirm dialog lives in the
 * client; the `devices.manage` gate and the online check live in the API. By
 * the time the agent sees the frame, the decision is made.
 */
exports.devicePowerSchema = zod_1.z.object({
    action: zod_1.z.enum(['shutdown', 'restart']),
});
//# sourceMappingURL=devices.js.map